import { _notNil } from '@/littledash';
import type { SendSeverityCounts } from '@/model/Agent.model';
import type { FC, ReactNode } from 'react';
import { RiCheckLine } from 'react-icons/ri';
import SendSeverityCountPills from './SendSeverityCountPills';
import styles from './sendResults.module.scss';

interface SendSectionHeaderProps {
  /** The section's position in the report, counted over the files and then the cross-domain checks. */
  number?: number;
  title: ReactNode;
  /** The heading level the section already used, so the report's outline does not change. */
  as: 'h2' | 'h3';
  /** The server's counts of the section's check findings by level. */
  severityCounts?: SendSeverityCounts;
  /** The server's verdict that the section's dataset passed validation exactly; the only thing that shows the tick. */
  exactPass?: boolean;
  titleTestId?: string;
}

/**
 * A numbered section row of a SEND report: `n. Title` on the left, and on the right the server's
 * finding counts by severity and, only on the server's exact-pass verdict for the dataset, a tick.
 * Nothing found is not a pass, so the tick is never inferred from empty counts.
 */
const SendSectionHeader: FC<SendSectionHeaderProps> = ({
  number,
  title,
  as: Heading,
  severityCounts,
  exactPass = false,
  titleTestId,
}) => (
  <div className={styles.sectionHeader} data-test-component="SendSectionHeader">
    <Heading className={styles.sectionTitle} data-testid={titleTestId}>
      {_notNil(number) && <span data-test-element="section-number">{number}.</span>}
      {/* A real space, so the heading's accessible name reads "1. Title" rather than "1.Title". */}
      {_notNil(number) && ' '}
      {title}
    </Heading>
    <span className={styles.sectionMarks}>
      <SendSeverityCountPills counts={severityCounts} />
      {exactPass && (
        <span className={styles.passPill} title="Passed validation" data-test-element="exact-pass">
          <RiCheckLine aria-hidden="true" />
          <span className="clip">Passed validation</span>
        </span>
      )}
    </span>
  </div>
);

export default SendSectionHeader;
